/**
 * Canvas Toolbox (V2)
 *
 * Compact floating button group + modal for adding nodes to the canvas.
 * Shows exactly 12 user-facing production node types in 3 categories:
 * - Agents (8): Character Designer, Location Designer, Object Designer, Image Generator,
 *               Video Generator, Sound Expert, Video Editor, Script Writer
 * - Media (3): Image Upload, Video Upload, Audio Upload
 * - Layout (1): Group
 */

import { useState, useMemo, useCallback, useRef, memo, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactFlow } from 'reactflow'
import { LayoutGrid, Search, Library, Download, History, Keyboard, ClipboardList } from 'lucide-react'
import { nodeSchemas, createNode, type RawNodeDataRecord, type NodeSchema } from './nodeRegistry'
import Modal from '../ui/Modal'
import { ProjectConfigGear } from './ProjectConfigGear'
import { cn } from '../../lib/utils'
import { ReferenceSelector } from './nodes/shared/ReferenceSelector'
import { NODE_TYPE_ICON } from './nodes/shared/nodeTypeIcons'
import { ClipDownloadModal } from './ClipDownloadModal'
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal'
import ProjectRulesModal from '../rules/ProjectRulesModal'
import { useProjectId } from '../../contexts/ProjectIdContext'
import { authenticatedFetch, getConfiguredApiBase } from '../../api/client'
import { createNadDebug } from '../../utils/debugLog'
import { toast } from 'sonner'
import { VersionHistoryPanel } from './VersionHistoryPanel'
import { useCanvasVersions } from '../../hooks/useCanvasVersions'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../hooks/useYCanvas'
import type { AutoConfirmCostSettings } from '../chat/autoConfirmCost'
import type { CanvasPerformanceSettings } from './canvasPerformanceSettings'
import { stripDirectAssetInputPortWrites } from './assetPortGuards'
import { useCanvasEdges } from './CanvasEdgesContext'

// Feature tag: library-import. Mirrors the backend's library-import
// structlog events so the trace runs end-to-end across the wire. Logs the
// keys of node_data returned by POST /library/nodes/{id}/import and the
// resolved picked_variant_index, confirming the user-picked variant
// actually crossed the response boundary.
const libraryImportLog = createNadDebug('dw:library-import:asset-manager')
const addNodeIconLog = createNadDebug('dw:add-node-icons')
const toolboxLog = createNadDebug('dw:canvas:toolbox')
const assetManagerLog = createNadDebug('dw:asset-manager:drawer')

interface ImportReferenceResponse {
  node_type: string
  node_data: RawNodeDataRecord
  assets_copied: number
}

/**
 * Upload node types that should open the OS file picker first instead of
 * creating an empty node. Maps frontend node type → media type + accept filter.
 */
const UPLOAD_NODE_TYPES: Record<string, { mediaType: 'image' | 'video' | 'audio'; accept: string }> = {
  image: { mediaType: 'image', accept: 'image/*' },
  video: { mediaType: 'video', accept: 'video/*' },
  audio: { mediaType: 'audio', accept: 'audio/*' },
}

interface CanvasToolboxProps {
  onAddNode: (node: ReturnType<typeof createNode>, explicitData?: RawNodeDataRecord) => void
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => void
  autoConfirmCostSettings?: AutoConfirmCostSettings
  onAutoConfirmImageCostChange?: (value: boolean) => void
  onAutoConfirmVideoCostChange?: (value: boolean) => void
  taskCompletionChimeEnabled?: boolean
  onTaskCompletionChimeEnabledChange?: (value: boolean) => void
  canvasPerformanceSettings?: CanvasPerformanceSettings
  onCanvasPerformanceSettingChange?: (
    key: keyof CanvasPerformanceSettings,
    value: boolean,
  ) => void
}

/**
 * Explicit allowlist of node types shown in the toolbox, mapped to their display category.
 * Only these 12 types appear — everything else is excluded regardless of schema.
 */
export const ALLOWED_TYPES: Record<string, string> = {
  characterDesigner: 'Agents',
  locationDesigner: 'Agents',
  objectDesigner: 'Agents',
  imageGenerator: 'Agents',
  videoGenerator: 'Agents',
  soundExpert: 'Agents',
  videoEditor: 'Agents',
  scriptWriter: 'Agents',
  image: 'Media',
  video: 'Media',
  audio: 'Media',
  nodeGroup: 'Layout',
}

/**
 * Display labels for categories — maps internal category key to i18n key.
 */
const CATEGORY_I18N_KEYS: Record<string, string> = {
  Agents: 'canvasToolbox.categoryAgents',
  Media: 'canvasToolbox.categoryMedia',
  Layout: 'canvasToolbox.categoryLayout',
}

export const CanvasToolbox = memo(function CanvasToolbox({
  onAddNode,
  onUploadFile,
  autoConfirmCostSettings,
  onAutoConfirmImageCostChange,
  onAutoConfirmVideoCostChange,
  taskCompletionChimeEnabled,
  onTaskCompletionChimeEnabledChange,
  canvasPerformanceSettings,
  onCanvasPerformanceSettingChange,
}: CanvasToolboxProps) {
  const { t } = useTranslation('canvas')
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAssetManagerOpen, setIsAssetManagerOpen] = useState(false)
  const [isClipDownloadOpen, setIsClipDownloadOpen] = useState(false)
  const { getViewport, getNodes, fitView } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const canvasEdges = useCanvasEdges()
  const toolboxRef = useRef<HTMLDivElement>(null)
  const projectId = useProjectId()
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
  const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false)
  const [isProjectRulesOpen, setIsProjectRulesOpen] = useState(false)
  const { versions, head, isLoading: isVersionsLoading, refresh: refreshVersions, createVersion, restoreVersion, deleteVersion, diffVersion, closeDiff, diffText, diffVersionId, isDiffLoading } = useCanvasVersions(projectId)

  // Hidden file input for upload-type nodes (file-picker-first pattern)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pendingMediaTypeRef = useRef<'image' | 'video' | 'audio'>('image')

  // Group schemas by allowed types only (allowlist approach)
  const groupedNodes = useMemo(() => {
    const groups: Record<string, Array<{ type: string; schema: NodeSchema }>> = {}
    for (const [type, category] of Object.entries(ALLOWED_TYPES)) {
      const schema = nodeSchemas[type]
      if (!schema) continue
      if (!groups[category]) groups[category] = []
      groups[category].push({ type, schema })
    }
    return groups
  }, [])

  // Apply search filter
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedNodes
    const q = searchQuery.toLowerCase()
    const out: typeof groupedNodes = {}
    for (const [cat, nodes] of Object.entries(groupedNodes)) {
      const hits = nodes.filter(({ type, schema }) =>
        schema.label.toLowerCase().includes(q) ||
        (schema.description && schema.description.toLowerCase().includes(q)) ||
        type.toLowerCase().includes(q)
      )
      if (hits.length) out[cat] = hits
    }
    return out
  }, [groupedNodes, searchQuery])

  const handleAddNode = useCallback((type: string) => {
    // For upload node types, open the OS file picker first instead of creating an empty node
    const uploadInfo = UPLOAD_NODE_TYPES[type]
    if (uploadInfo && onUploadFile && fileInputRef.current) {
      pendingMediaTypeRef.current = uploadInfo.mediaType
      fileInputRef.current.accept = uploadInfo.accept
      fileInputRef.current.click()
      setIsOpen(false)
      return
    }

    const vp = getViewport()
    // Measure the actual canvas container width via closest .react-flow ancestor
    const rfContainer = toolboxRef.current?.closest('.react-flow')
    const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
    const cx = (-vp.x + cw / 2) / vp.zoom
    const cy = (-vp.y + window.innerHeight / 2) / vp.zoom

    // Collision avoidance: nudge position until free of existing nodes
    const OFFSET_STEP = 40
    let offsetX = 0
    let offsetY = 0
    const existingPositions = getNodes().map(n => n.position)
    for (let i = 0; i < 10; i++) {
      const testX = cx + offsetX
      const testY = cy + offsetY
      const tooClose = existingPositions.some(
        p => Math.abs(p.x - testX) < 50 && Math.abs(p.y - testY) < 50
      )
      if (!tooClose) break
      offsetX += OFFSET_STEP
      offsetY += OFFSET_STEP
    }

    const position = { x: cx + offsetX, y: cy + offsetY }
    const newNode = createNode(type, position)
    toolboxLog({
      event: 'add-node-dispatched',
      nodeType: type,
      nodeId: newNode.id,
      x: position.x,
      y: position.y,
    })
    onAddNode(newNode, {})

    // Auto-focus on the newly created node
    requestAnimationFrame(() => {
      fitView({
        nodes: [{ id: newNode.id, position, data: {} }],
        padding: 0.3,
        duration: 500,
        maxZoom: vp.zoom,
      })
    })

    setIsOpen(false)
  }, [getViewport, getNodes, fitView, onAddNode, onUploadFile])

  /** Called when the user picks a file (or cancels — files will be empty). */
  const handleFileSelected = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onUploadFile?.(file, pendingMediaTypeRef.current)
    }
    // Reset so the same file can be selected again
    e.target.value = ''
  }, [onUploadFile])

  const handleToggleAddNode = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        toolboxLog({ event: 'add-node-modal-closed-via-toggle' })
        return false
      }
      const nodeTypes = Object.keys(ALLOWED_TYPES)
      const missingSvgIconTypes = nodeTypes.filter((type) => !NODE_TYPE_ICON[type])
      const svgIconCount = nodeTypes.length - missingSvgIconTypes.length
      addNodeIconLog({
        trace_id: `add-node-icons:toolbox:${svgIconCount}-of-${nodeTypes.length}`,
        event: missingSvgIconTypes.length === 0
          ? 'add-node-modal-opened-svg-icons-complete'
          : 'add-node-modal-opened-svg-icons-incomplete',
        total_node_types: nodeTypes.length,
        svg_icon_count: svgIconCount,
        missing_svg_icon_types: missingSvgIconTypes,
      })
      setSearchQuery('')
      return true
    })
  }, [])

  const handleToggleAssetManager = useCallback(() => {
    setIsAssetManagerOpen((prev) => {
      const next = !prev
      assetManagerLog({
        event: next ? 'asset-manager-opened' : 'asset-manager-closed',
        trigger: 'dock-toggle',
        previous_open: prev,
        next_open: next,
      })
      return next
    })
  }, [])

  const handleCloseAssetManager = useCallback((trigger: string) => {
    setIsAssetManagerOpen((prev) => {
      if (!prev) return prev
      assetManagerLog({
        event: 'asset-manager-closed',
        trigger,
        previous_open: prev,
        next_open: false,
      })
      return false
    })
  }, [])

  const handleToggleClipDownload = useCallback(() => {
    setIsClipDownloadOpen((prev) => !prev)
  }, [])

  const handleToggleVersionHistory = useCallback(() => {
    setIsVersionHistoryOpen((prev) => !prev)
  }, [])

  const handleToggleKeyboardShortcuts = useCallback(() => {
    setIsKeyboardShortcutsOpen((prev) => !prev)
  }, [])

  const handleToggleProjectRules = useCallback(() => {
    setIsProjectRulesOpen((prev) => !prev)
  }, [])

  const handleDeleteFromLibrary = useCallback(async (nodeId: string) => {
    try {
      const resp = await authenticatedFetch(
        `${getConfiguredApiBase()}/library/nodes/${nodeId}`,
        { method: 'DELETE' }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => null)
        throw new Error(err?.detail || resp.statusText)
      }
      toast.success(t('canvasToolbox.removedFromLibrary'))
    } catch (err) {
      toast.error(t('canvasToolbox.failedToDelete', { message: err instanceof Error ? err.message : 'Unknown error' }))
    }
  }, [t])

  const handleAddToCanvas = useCallback(
    async (references: { nodeId: string; nodeType: string; label: string; assetPath: string; mediaType: string }[]) => {
      if (references.length === 0) return

      // Close modal immediately for responsive feel
      assetManagerLog({
        event: 'asset-manager-closed',
        trigger: 'add-to-canvas',
        count: references.length,
      })
      setIsAssetManagerOpen(false)

      const toastId = toast.loading(t('canvasToolbox.addingToCanvas'))

      // Calculate viewport center for positioning
      const vp = getViewport()
      const rfContainer = toolboxRef.current?.closest('.react-flow')
      const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
      const baseCx = (-vp.x + cw / 2) / vp.zoom
      const baseCy = (-vp.y + window.innerHeight / 2) / vp.zoom

      const apiBase = getConfiguredApiBase()
      let added = 0

      for (let i = 0; i < references.length; i++) {
        const ref = references[i]
        try {
          // Call backend to copy assets and get full node data
          const resp = await authenticatedFetch(
            `${apiBase}/library/nodes/${ref.nodeId}/import`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ target_project_id: projectId }),
            }
          )

          if (!resp.ok) {
            const err = await resp.json().catch(() => null)
            console.error(`[AssetManager] Import failed for ${ref.nodeId}:`, err?.detail || resp.statusText)
            continue
          }

          const { node_type, node_data }: ImportReferenceResponse = await resp.json()
          const { data: importNodeData, strippedFields } = stripDirectAssetInputPortWrites(
            node_type,
            node_data,
          )
          const nodeDataKeys = Object.keys(importNodeData)

          libraryImportLog({
            trace_id: `library-import:${node_type}`,
            event: 'import-response-received',
            libraryNodeId: ref.nodeId,
            node_type,
            node_data_key_count: nodeDataKeys.length,
            has_out_image: Object.prototype.hasOwnProperty.call(importNodeData, 'out_image'),
            has_legacy_out_character_sheet: Object.prototype.hasOwnProperty.call(importNodeData, 'out_character_sheet'),
            node_data_keys: nodeDataKeys,
            picked_variant_index: typeof importNodeData.picked_variant_index === 'number'
              ? importNodeData.picked_variant_index
              : null,
            stripped_edge_backed_inputs: strippedFields,
          })

          // Offset each node to avoid stacking
          const position = {
            x: baseCx + i * 60,
            y: baseCy + i * 60,
          }

          onAddNode(createNode(node_type, position, importNodeData), importNodeData)
          added++
        } catch (error) {
          console.error(`[AssetManager] Import error for ${ref.nodeId}:`, error)
        }
      }

      if (added > 0) {
        toast.success(added === 1 ? t('canvasToolbox.addedToCanvas') : t('canvasToolbox.addedCountToCanvas', { count: added }), { id: toastId })
      } else {
        toast.error(t('canvasToolbox.failedToAddToCanvas'), { id: toastId })
      }
    },
    [getViewport, onAddNode, projectId, t]
  )

  const hasResults = Object.keys(filteredGroups).length > 0

  return (
    <>
      {/* Hidden file input for upload-type nodes (file-picker-first) */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelected}
        className="hidden"
        data-testid="toolbox-upload-file-input"
      />

      {/* Compact toolbox buttons — all dock icons now toggle for uniform drawer dismiss logic (bug 1573341771089787) */}
      <div ref={toolboxRef} className="flex flex-col items-center gap-1 p-1.5 bg-card border border-border rounded-md">
        <button
          onClick={handleToggleAddNode}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title={t('canvasToolbox.addNodes')}
          data-testid="toolbox-add-nodes"
        >
          <LayoutGrid className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={handleToggleAssetManager}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group',
            isAssetManagerOpen && 'bg-muted'
          )}
          title={t('canvasToolbox.assetManager')}
          data-testid="toolbox-asset-manager"
        >
          <Library className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={handleToggleClipDownload}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title={t('canvasToolbox.downloadAssets')}
          data-testid="toolbox-download-clips"
        >
          <Download className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={handleToggleVersionHistory}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title={t('canvasToolbox.versionHistory')}
          data-testid="toolbox-version-history"
        >
          <History className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={handleToggleKeyboardShortcuts}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title={t('canvasToolbox.keyboardShortcuts')}
          aria-label={t('canvasToolbox.keyboardShortcuts')}
          data-testid="toolbox-keyboard-shortcuts"
        >
          <Keyboard className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <button
          onClick={handleToggleProjectRules}
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            'hover:bg-muted transition-colors cursor-pointer group'
          )}
          title={t('canvasToolbox.projectRules')}
          aria-label={t('canvasToolbox.projectRules')}
          data-testid="toolbox-project-rules"
        >
          <ClipboardList className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
        </button>

        <ProjectConfigGear
          autoConfirmCostSettings={autoConfirmCostSettings}
          onAutoConfirmImageCostChange={onAutoConfirmImageCostChange}
          onAutoConfirmVideoCostChange={onAutoConfirmVideoCostChange}
          taskCompletionChimeEnabled={taskCompletionChimeEnabled}
          onTaskCompletionChimeEnabledChange={onTaskCompletionChimeEnabledChange}
          canvasPerformanceSettings={canvasPerformanceSettings}
          onCanvasPerformanceSettingChange={onCanvasPerformanceSettingChange}
        />

      </div>

      {/* Add Node modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={t('canvasToolbox.addNodeTitle')} size="md">
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('canvasToolbox.searchNodes')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'w-full pl-9 pr-3 py-2 text-sm bg-muted border border-border rounded-lg',
                'text-foreground placeholder-muted-foreground',
                'focus:outline-none focus:border-primary/50 transition-colors'
              )}
              autoFocus
              data-testid="toolbox-search"
            />
          </div>

          {/* Node list */}
          <div className="max-h-72 overflow-y-auto -mx-1 px-1">
            {!hasResults && (
              <div className="py-8 text-center text-muted-foreground text-xs font-medium" data-testid="toolbox-empty-state">
                {searchQuery ? t('canvasToolbox.noMatches') : t('canvasToolbox.noNodesAvailable')}
              </div>
            )}

            {Object.entries(filteredGroups).map(([category, categoryNodes]) => (
              <div key={category} className="mb-2" data-testid={`toolbox-category-${category}`}>
                <div className="px-1 pt-2 pb-1.5">
                  <span className="text-xxs font-medium text-muted-foreground uppercase tracking-wider">
                    {CATEGORY_I18N_KEYS[category] ? t(CATEGORY_I18N_KEYS[category]) : category}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {categoryNodes.map(({ type, schema }) => (
                    <button
                      key={type}
                      onClick={() => handleAddNode(type)}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-2 rounded-lg',
                        'hover:bg-muted cursor-pointer transition-colors group text-left'
                      )}
                      title={schema.description || `Add ${schema.label}`}
                      data-testid={`toolbox-node-${type}`}
                    >
                      <span
                        className={cn(
                          'h-4 w-4 shrink-0 text-muted-foreground opacity-70 transition-opacity',
                          'group-hover:text-foreground group-hover:opacity-100',
                          '[&>svg]:h-4 [&>svg]:w-4'
                        )}
                        aria-hidden="true"
                        data-testid={`toolbox-node-icon-${type}`}
                      >
                        {NODE_TYPE_ICON[type] ?? NODE_TYPE_ICON.nodeGroup}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground truncate transition-colors">
                        {schema.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Asset Manager modal — now dismisses on backdrop click (bug 1573341771089787) and logs drawer events.
          Precise trigger (backdrop-click / close-button / escape-key) is logged in ReferenceSelector layer;
          toolbox layer logs generic selector-dismiss to avoid muddy trigger fidelity. */}
      <ReferenceSelector
        isOpen={isAssetManagerOpen}
        onClose={() => handleCloseAssetManager('selector-dismiss')}
        onSave={() => {}}
        excludeNodeId=""
        canvasNodes={getNodes()}
        mode="asset-manager"
        onAddToCanvas={handleAddToCanvas}
        onDeleteFromLibrary={handleDeleteFromLibrary}
        maxReferences={99}
      />

      {/* Keyboard Shortcuts directory modal */}
      <KeyboardShortcutsModal
        isOpen={isKeyboardShortcutsOpen}
        onClose={() => setIsKeyboardShortcutsOpen(false)}
      />

      {/* Project rules modal */}
      <ProjectRulesModal
        isOpen={isProjectRulesOpen}
        onClose={() => setIsProjectRulesOpen(false)}
      />

      {/* Clip Download modal */}
      <ClipDownloadModal
        isOpen={isClipDownloadOpen}
        onClose={() => setIsClipDownloadOpen(false)}
        canvasNodes={getNodes()}
        canvasEdges={canvasEdges}
      />

      {/* Version History panel */}
      <VersionHistoryPanel
        isOpen={isVersionHistoryOpen}
        onClose={() => { closeDiff(); setIsVersionHistoryOpen(false) }}
        versions={versions}
        currentVersionId={head}
        onCreateVersion={createVersion}
        onRestoreVersion={restoreVersion}
        onDeleteVersion={deleteVersion}
        onDiffVersion={diffVersion}
        onCloseDiff={closeDiff}
        onRefresh={refreshVersions}
        diffText={diffText}
        diffVersionId={diffVersionId}
        isDiffLoading={isDiffLoading}
        isLoading={isVersionsLoading}
      />
    </>
  )
})
